import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { SharedAPI } from '@shared-features/services';
import { Country } from '@shared-features/models';
import { MobileLengthPerCode } from '@shared-features/enums/shared-enums.enum';
import { FunctionUtil } from '@shared/utils';
import { MobileNumberLengthValidator, PhoneNumberValidatorWithException } from '@shared/utils/form-validators.util';

export interface PhoneNumberValue {
  countryCode: string;
  mobileNumber: string;
}

@Component({
  selector: 'app-phone-number-field',
  templateUrl: './phone-number-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberFieldComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberFieldComponent
  extends BaseComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges
{
  @Input() countries: Country[];
  @Input() placeholder = '5X XXX XXXX';
  @Input() required = true;

  form: FormGroup;
  protected readonly MobileLengthPerCode = MobileLengthPerCode;

  private outerControl: AbstractControl | null = null;

  private onChange: (value: PhoneNumberValue) => void = () => null;
  private onTouched: () => void = () => null;
  private onValidatorChange: () => void = () => null;

   get control(): AbstractControl | null {
    if (!this.outerControl) {
      this.outerControl = this.injector.get(NgControl, null)?.control ?? null;
    }
    return this.outerControl;
  }

  constructor(private formBuilder: FormBuilder, private sharedAPI: SharedAPI) {
    super();
  }

  ngOnInit(): void {
    const formatRules = [
      PhoneNumberValidatorWithException,
      MobileNumberLengthValidator(() => this.form?.value.countryCode),
    ];

    this.form = this.formBuilder.group({
      countryCode: ['', this.required ? [Validators.required] : []],
      mobileNumber: ['', this.required ? [Validators.required, ...formatRules] : formatRules],
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: PhoneNumberValue) => {
      this.form.get('mobileNumber').updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.onChange(value);
      this.onValidatorChange();
    });

    if (!this.countries) {
      this.load(this.sharedAPI.getAllCountries())
        .pipe(takeUntil(this.destroy$))
        .subscribe((countries) => {
          this.countries = countries;
          this.applyDefaultCountryCode();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.countries && this.countries?.length) {
      this.applyDefaultCountryCode();
    }
  }

  writeValue(value: PhoneNumberValue | null): void {
    this.form?.patchValue(
      { countryCode: value?.countryCode ?? '', mobileNumber: value?.mobileNumber ?? '' },
      { emitEvent: false }
    );
    this.applyDefaultCountryCode();
  }

  registerOnChange(fn: (value: PhoneNumberValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form?.disable({ emitEvent: false }) : this.form?.enable({ emitEvent: false });
  }

  validate(): ValidationErrors | null {
    if (!this.form) {
      return null;
    }
    // Merged, not `??` — a missing country code was invisible to the outer control whenever the
    // number was also invalid, since only the first operand was ever returned.
    const errors = { ...this.form.get('mobileNumber').errors, ...this.form.get('countryCode').errors };
    return Object.keys(errors).length ? errors : null;
  }

  markTouched(): void {
    this.onTouched();
  }

  private applyDefaultCountryCode(): void {
    if (!this.form || !this.countries?.length || this.form.value.countryCode) {
      return;
    }
    Promise.resolve().then(() => {
      if (this.form.value.countryCode) {
        return;
      }
      this.form.get('countryCode').setValue(FunctionUtil.getSaudiArabiaPhoneKey(this.countries));
    });
  }
}
